import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getLoyaltyStatus, getOrderHistory } from "../services/orderPortal";
import { trackShiprocketOrder } from "../services/shiprocketTracking";
import { parseProductIdsFromOrderItems } from "../utils/parseOrderProductIds";
import {
  clearCustomerSession,
  getCustomerSession,
} from "../utils/customerSession";
import { formatInrAmount } from "../utils/formatInrAmount";
import { BtnContent } from "../components/BtnIcon";
import { IoLocateOutline, IoLogOutOutline, IoStorefrontOutline } from "react-icons/io5";

/** Orders table: dd/mm/yyyy hh:mm:ss AM|PM (12-hour, local time). */
function formatSheetDate(v) {
  if (v == null || v === "") return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const pad = (n) => String(n).padStart(2, "0");
  const day = pad(d.getDate());
  const month = pad(d.getMonth() + 1);
  const year = d.getFullYear();
  let h12 = d.getHours() % 12;
  if (h12 === 0) h12 = 12;
  const tt = d.getHours() >= 12 ? "PM" : "AM";
  return `${day}/${month}/${year} ${pad(h12)}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${tt}`;
}

const AWB_FIELDS = [
  "awb",
  "awb no",
  "awb code",
  "awb number",
  "courier awb",
  "shiprocket awb",
  "tracking id",
  "tracking number",
  "tracking no",
  "tracking code",
];

const SHIPMENT_FIELDS = [
  "shipment id",
  "shipment_id",
  "shiprocket shipment id",
  "shiprocket shipment_id",
];

const TRACKING_URL_FIELDS = [
  "tracking url",
  "tracking link",
  "track url",
  "track link",
  "shiprocket tracking url",
];

function normalizeFieldName(value) {
  return String(value || "")
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function getRowField(row, fieldNames) {
  const wanted = new Set(fieldNames.map(normalizeFieldName));
  for (const [key, value] of Object.entries(row || {})) {
    if (!wanted.has(normalizeFieldName(key))) continue;
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function getTrackingIdentifiers(row) {
  return {
    awb: getRowField(row, AWB_FIELDS),
    shipmentId: getRowField(row, SHIPMENT_FIELDS),
    trackingUrl: getRowField(row, TRACKING_URL_FIELDS),
  };
}

function TrackingCell({ row }) {
  const [state, setState] = useState({ status: "idle", data: null, message: "" });
  const { awb, shipmentId, trackingUrl } = getTrackingIdentifiers(row);
  const summary = state.data?.summary || {};
  const trackUrl = summary.trackUrl || trackingUrl;

  const loadTracking = async () => {
    if (!awb && !shipmentId) return;
    setState({ status: "loading", data: null, message: "" });
    const result = await trackShiprocketOrder({ awb, shipmentId });
    if (!result.ok) {
      setState({
        status: "error",
        data: null,
        message: result.message || "Tracking unavailable.",
      });
      return;
    }
    setState({ status: "loaded", data: result.data, message: "" });
  };

  if (!awb && !shipmentId && !trackingUrl) {
    return <span className="text-muted">Pending</span>;
  }

  return (
    <div className="small" style={{ minWidth: "150px" }}>
      {(awb || shipmentId) && (
        <button
          type="button"
          className="btn btn-outline-primary btn-sm py-0 px-2"
          onClick={loadTracking}
          disabled={state.status === "loading"}
        >
          <BtnContent icon={IoLocateOutline} iconSize={14}>
            {state.status === "loading" ? "Tracking…" : "Track"}
          </BtnContent>
        </button>
      )}
      {awb && <div className="text-muted mt-1">AWB: {awb}</div>}
      {!awb && shipmentId && <div className="text-muted mt-1">Shipment: {shipmentId}</div>}
      {state.status === "loaded" && (
        <div className="mt-2">
          <div className="fw-semibold">{summary.status || "Tracking available"}</div>
          {summary.courier && <div className="text-muted">{summary.courier}</div>}
          {summary.activity && <div className="text-muted">{summary.activity}</div>}
          {summary.location && <div className="text-muted">{summary.location}</div>}
          {summary.lastUpdate && <div className="text-muted">{formatSheetDate(summary.lastUpdate)}</div>}
        </div>
      )}
      {state.status === "error" && (
        <div className="text-danger mt-1">{state.message}</div>
      )}
      {trackUrl && (
        <a href={trackUrl} target="_blank" rel="noopener noreferrer" className="d-block mt-1">
          Open tracking
        </a>
      )}
    </div>
  );
}

function OrderHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [placedFlash, setPlacedFlash] = useState(null);

  const session = getCustomerSession();

  useEffect(() => {
    const st = location.state;
    if (st?.orderPlaced) {
      setPlacedFlash({
        orderId: st.orderId,
        warning: st.orderWarning || null,
        guestEmail: st.guestEmail || null,
      });
      navigate("/orders", { replace: true, state: {} });
      return;
    }
    if (!getCustomerSession()) {
      navigate("/login", { replace: true });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (!session) {
      setLoading(false);
      setOrders([]);
      setLoyalty(null);
      setError(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const [hist, loy] = await Promise.all([
        getOrderHistory(session.email, session.token),
        getLoyaltyStatus(session.email, session.token),
      ]);
      if (cancelled) return;
      setLoading(false);
      if (!hist.ok) {
        setError(hist.message || "Could not load orders.");
        setOrders([]);
      } else {
        setOrders(Array.isArray(hist.data?.orders) ? hist.data.orders : []);
      }
      if (loy.ok && loy.data) {
        setLoyalty(loy.data);
      } else {
        setLoyalty(null);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- session from getCustomerSession() on each render
  }, [session?.email, session?.token]);

  const logout = () => {
    clearCustomerSession();
    setPlacedFlash(null);
    navigate("/login", { replace: true });
  };

  const guestAfterOrder = placedFlash?.guestEmail && !session;

  if (!session && !guestAfterOrder) {
    return null;
  }

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <h1 className="h3 mb-0">Your orders</h1>
        <div className="d-flex gap-2">
          {session && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={logout}>
                <BtnContent icon={IoLogOutOutline}>Log out</BtnContent>
              </button>
              <Link to="/shop" className="btn btn-primary btn-sm">
                <BtnContent icon={IoStorefrontOutline}>Shop</BtnContent>
              </Link>
            </>
          )}
          {guestAfterOrder && (
            <Link to="/shop" className="btn btn-primary btn-sm">
              <BtnContent icon={IoStorefrontOutline}>Shop</BtnContent>
            </Link>
          )}
        </div>
      </div>

      {placedFlash && (
        <div className="alert alert-success mb-4" role="status">
          <strong>Order placed.</strong>{" "}
          {placedFlash.orderId ? `Order ID: ${placedFlash.orderId}. ` : ""}
          {placedFlash.warning && (
            <span className="d-block small mt-1 text-dark">{placedFlash.warning}</span>
          )}
          {guestAfterOrder && (
            <p className="small mb-0 mt-2">
              Log in with <strong>{placedFlash.guestEmail}</strong> to see your full order history and
              loyalty progress.{" "}
              <Link to="/login" className="alert-link">
                Log in
              </Link>
            </p>
          )}
        </div>
      )}

      {session && (
        <p className="small text-muted mb-4">Signed in as {session.email}</p>
      )}

      {session && loyalty && (
        <div className="card shadow-sm border-0 mb-4" style={{ background: "#fffef5" }}>
          <div className="card-body">
            <h2 className="h5 mb-2">Loyalty card</h2>
            <p className="mb-2 small">
              Every <strong>{loyalty.loyaltyEvery || 6}</strong> completed orders on this email, your
              next checkout can use a <strong>₹{formatInrAmount(loyalty.loyaltyVoucherAmount || 750)}</strong>{" "}
              voucher off the item total when you are logged in with the same email.
            </p>
            <p className="mb-0 fw-semibold">
              {loyalty.nextLoyaltyVoucher || loyalty.nextOrderIsFree
                ? `Your next checkout can apply the ₹${formatInrAmount(loyalty.loyaltyVoucherAmount || 750)} voucher — tick the option on checkout when logged in.`
                : `Orders on this account: ${loyalty.paidOrderCount}. Place ${loyalty.ordersUntilNextVoucher ?? loyalty.ordersUntilNextFree ?? 0} more for the next voucher.`}
            </p>
          </div>
        </div>
      )}

      {!session && guestAfterOrder ? (
        <p className="text-muted small mb-0">
          After you log in, your new order will appear in the table below with the rest of your history.
        </p>
      ) : (
        <>
          {loading && <p className="text-muted">Loading…</p>}
          {error && <p className="text-danger small">{error}</p>}

          {!loading && !error && orders.length === 0 && (
            <p className="text-muted">No orders found for this email yet.</p>
          )}

          {!loading && orders.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Order ID</th>
                    <th>Total</th>
                    <th>Items</th>
                    <th>Tracking</th>
                    <th>Review</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((row, idx) => (
                    <tr key={`${String(row.orderid || idx)}-${idx}`}>
                      <td className="small">{formatSheetDate(row.timestamp)}</td>
                      <td className="small font-monospace">{String(row.orderid || "—")}</td>
                      <td className="small price-figure">
                        {row.total != null && row.total !== ""
                          ? `₹${formatInrAmount(row.total)}`
                          : "—"}
                      </td>
                      <td className="small text-truncate" style={{ maxWidth: "220px" }}>
                        {String(row.items || "—")}
                      </td>
                      <td className="small">
                        <TrackingCell row={row} />
                      </td>
                      <td className="small">
                        {(() => {
                          const pids = parseProductIdsFromOrderItems(row.items);
                          if (pids.length === 0) return <span className="text-muted">—</span>;
                          return pids.map((pid) => (
                            <Link
                              key={`${row.orderid}-${pid}`}
                              to={`/product/${encodeURIComponent(pid)}`}
                              state={{
                                openReview: true,
                                prefillOrderId: String(row.orderid || "").trim(),
                              }}
                              className="d-block"
                            >
                              Rate {pid}
                            </Link>
                          ));
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <p className="mt-4 mb-0">
        <Link to="/cart" className="small text-muted">
          ← Cart
        </Link>
      </p>
    </div>
  );
}

export default OrderHistory;
